/**
 * EditorDocument → PSD export merge algorithm. The pure structural
 * walk lives in `buildExportPsd` (no DOM, no Fabric); orchestration
 * (loading the stash, calling ag-psd's `writePsd`) lives in
 * `exportDocumentToPsd`.
 *
 * ## The merge contract
 *
 * For each editor `LayerNode`, decide whether to emit it as:
 *
 *  1. **Restored from stash** — the layer has a `layerOriginById`
 *     entry from import time AND `rasterModified !== true`. The
 *     original PSD layer record from the re-parsed stash is reused
 *     verbatim (preserving adjustment data, smart-object metadata,
 *     text data, vector masks, layer effects, and any unmapped
 *     chunks the editor never modeled), with the editor's extrinsic
 *     properties (opacity, blend mode, visibility, name) overlaid.
 *
 *  2. **Raster** — the layer was editor-created (no stash origin) OR
 *     its `rasterModified` flag was flipped (paint, retouch, paint-
 *     bucket, non-affine commit, destructive adjustment Apply,
 *     in-place AI op). The caller-supplied `layerCanvasProvider`
 *     returns the layer's current pixels, which are written as a
 *     fresh raster layer.
 *
 * Groups always emit fresh structure from the editor tree (the user
 * may have reorganized layers since import), but if a group node
 * still carries a stash origin its PSD-level chunks are spread in
 * before the editor's extrinsic overlay.
 *
 * ## Layer order
 *
 * The editor's `rootIds` / group `childIds` arrays are top-first
 * (matches Photoshop's panel display); ag-psd's `children` is
 * bottom-to-top (matches the PSD file format's serialization order).
 * `buildExportPsd` reverses on emit so the output PSD's stacking
 * order matches what the editor displayed.
 *
 * ## Document-level metadata
 *
 * When `originalPsd` is provided, `buildExportPsd` returns a spread
 * of the original Psd with only `width`, `height`, and `children`
 * overridden. This preserves ICC profile, layer comps, paths panel
 * data, alpha channels, guides, slices, and any other doc-level
 * resources ag-psd surfaces — by NOT touching them.
 *
 * When `originalPsd` is null (editor-created document, or stash
 * absent), a minimal RGB 8-bit Psd shape is synthesized.
 */

import type { Psd, Layer as PsdLayer, BlendMode as PsdBlendMode } from 'ag-psd';
import type { BlendMode as EditorBlendMode, EditorDocument, LayerNode } from '../types';
import { loadPsdStash, type PsdLayerOrigin } from './psd-source-stash';
import { unmapEditorAdjustment } from './psd-adjustment-map';
import { buildPsdTextRecord, type PsdExportTextPayload } from './psd-text-export';
import { buildPsdVectorChunks } from './psd-vector-export';

/** Provider callback for text-layer export. Caller (EditorPage's
 *  handleExportPsd) queries the Fabric IText bound to a layer
 *  and returns the same payload shape SVG export uses
 *  (`SvgExportTextPayload`); we accept the structural twin
 *  `PsdExportTextPayload` here. Returning null falls back to
 *  raster — used when the IText is arched / path-bound / multi-
 *  line in ways the native PSD path can't carry. */
export type PsdLayerTextProvider = (layerId: string) => PsdExportTextPayload | null;

/** Per-layer payload returned by `layerCanvasProvider`. The provider
 *  is the only DOM-touching dependency of `buildExportPsd`; tests
 *  inject a synthetic provider that returns plain objects.
 *
 *  The payload is consulted for EVERY leaf layer, not just raster-
 *  emitted ones — `left`/`top` overlay the stash's original position
 *  on stash-restored layers too, so a user moving a stash-backed
 *  layer in the editor doesn't see the move silently revert on
 *  export. (Earlier versions of this contract only passed the
 *  payload through for raster emission, which lost user position
 *  changes on stash-restored layers — a Critical post-review
 *  finding.) */
export interface PsdExportRasterPayload {
  /** The layer's current pixels. ag-psd accepts either a real
   *  `HTMLCanvasElement` or a `PixelData`-shape; tests use the
   *  latter to avoid the canvas2d requirement. */
  canvas: HTMLCanvasElement;
  /** Top-left position of the layer's bbox in document coordinates.
   *  MUST be the top-left (not the center or another origin) and in
   *  document scene coordinates (not viewport). Caller should call
   *  `obj.setCoords()` before reading `getBoundingRect()` to avoid
   *  stale-cache issues mid-drag. */
  left: number;
  top: number;
}

export type LayerCanvasProvider = (layerId: string) => PsdExportRasterPayload | null;

/** Provider callback for the raster pixels of a layer's mask. Returns
 *  the mask's RAW grayscale canvas (white = visible, black = hidden)
 *  in document scene coordinates at full document size. The export
 *  applies invert / density / feather on top before assigning to the
 *  PSD layer's `mask` field. Returning null means "no mask to emit"
 *  — either the layer has no mask, the mask is `enabled: false`, or
 *  the canvas isn't queryable (transient state mid-load). */
export type PsdLayerMaskProvider = (layerId: string) => HTMLCanvasElement | null;

/** Provider callback for primitive shape layers (Rect / Circle /
 *  Triangle). Returns a `VectorPath` derived from the Fabric shape
 *  in scene coords; the export emits it via the same PSD vector
 *  chunks the pen-path branch uses. Returning null falls back to
 *  raster (e.g. for `fabric.Line`, which is open-path and would
 *  need PSD's open-path support to round-trip parametrically). */
export type PsdLayerShapePathProvider = (layerId: string) => import('../types').VectorPath | null;

/** Apply a layer-mask emission onto an in-flight PsdLayer. Reads
 *  `LayerNode.mask` and converts to ag-psd's `LayerMaskData` shape:
 *
 *  - `inverted`: applied at pixel time (PSD masks don't have an
 *    explicit invert flag — the canvas's pixel values directly
 *    drive visibility).
 *  - `density` (0..1): mapped to `userMaskDensity` (0..255 byte).
 *  - `feather` (px): forwarded as `userMaskFeather`.
 *  - `linked` (transforms with layer): maps to `positionRelativeToLayer`.
 *  - `enabled: false`: emits `disabled: true` so PSD viewers know
 *    not to apply the mask.
 *
 *  Vector masks (`kind === 'vector'`) are NOT handled here — the
 *  PSD `vectorMask` slot is already claimed by the layer's primary
 *  vector geometry (pen path / shape). For v1, vector masks fall
 *  back to their RAW raster representation, which the editor's
 *  `materializeMaskClipPath` keeps in sync. */
/** Parse a hex color `#rrggbb` (or shorthand `#rgb`) into RGBA. The
 *  editor stores effect colors as hex strings; ag-psd expects a
 *  structured Color (RGBA most commonly). Alpha is opaque since the
 *  editor doesn't surface a separate alpha per effect — opacity
 *  lives on the effect's own `opacity` field instead. */
function hexToRgba(hex: string): { r: number; g: number; b: number; a: number } {
  let h = (hex || '').replace('#', '').trim();
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (h.length !== 6) return { r: 0, g: 0, b: 0, a: 255 };
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return { r: 0, g: 0, b: 0, a: 255 };
  }
  return { r, g, b, a: 255 };
}

/** Emit editor-side `LayerNode.effects` as ag-psd's `effects` chunk.
 *  Without this, drop shadow / stroke / glow set in the editor are
 *  silently dropped on save — stash-restored PSD effects survive
 *  (they ride along on the stash entry), but anything the user
 *  added or edited is lost.
 *
 *  Mapping:
 *    - dropShadow → effects.dropShadow[]. PSD encodes offset as
 *      angle + distance instead of x/y; convert at emission time.
 *    - stroke → effects.stroke[] with `position: 'outside'`
 *      (Photoshop's default for shape strokes; the editor's
 *      single-knob stroke effect doesn't distinguish inside / center
 *      / outside in v1).
 *    - glow → effects.outerGlow. Editor's `intensity` (0.5–3) maps
 *      to opacity in [0,1] by clamping `intensity / 3`.
 *
 *  Stash-overlay note: the editor's effects are AUTHORITATIVE for
 *  layers the user has touched. We REPLACE the stash's effects
 *  rather than merge so a user disabling drop-shadow doesn't see
 *  the PSD original's drop-shadow leak back in on save.
 *  Compromise: only replace when `node.effects` is set (truthy);
 *  layers with no editor-side effects fall through to whatever the
 *  stash carried. */
function applyEffectsToLayer(base: PsdLayer, node: LayerNode): PsdLayer {
  const fx = node.effects;
  if (!fx) return base;
  const out: PsdLayer = { ...base };
  const psdEffects: NonNullable<PsdLayer['effects']> = { ...(base.effects ?? {}) };
  if (fx.dropShadow) {
    const c = hexToRgba(fx.dropShadow.color);
    const dx = fx.dropShadow.offsetX;
    const dy = fx.dropShadow.offsetY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    // PSD measures shadow angle in degrees with the positive x-axis
    // pointing right and 0° pointing the light at the shadow's
    // OPPOSITE direction (i.e. shadow falls 180° from the light).
    // Editor stores offset directly; convert offset→angle so a
    // user-positioned shadow round-trips visually.
    const angle = distance > 0 ? (Math.atan2(-dy, dx) * 180) / Math.PI : 120;
    psdEffects.dropShadow = [
      {
        present: true,
        enabled: true,
        color: c,
        size: { units: 'Pixels', value: Math.max(0, fx.dropShadow.blur) },
        distance: { units: 'Pixels', value: distance },
        angle,
        opacity: 1,
        blendMode: 'multiply',
        useGlobalLight: false,
      },
    ];
  }
  if (fx.stroke) {
    const c = hexToRgba(fx.stroke.color);
    psdEffects.stroke = [
      {
        present: true,
        enabled: true,
        size: { units: 'Pixels', value: Math.max(0, fx.stroke.width) },
        position: 'outside',
        fillType: 'color',
        color: c,
        opacity: 1,
        blendMode: 'normal',
      },
    ];
  }
  if (fx.glow) {
    const c = hexToRgba(fx.glow.color);
    psdEffects.outerGlow = {
      present: true,
      enabled: true,
      color: c,
      size: { units: 'Pixels', value: Math.max(0, fx.glow.blur) },
      // Editor stores intensity in 0.5..3 range — clamp into the
      // PSD opacity unit (0..1). Matches the editor's visual weight
      // since intensity primarily drives alpha amplification.
      opacity: Math.max(0, Math.min(1, fx.glow.intensity / 3)),
      blendMode: 'screen',
    };
  }
  out.effects = psdEffects;
  return out;
}

function applyMaskToLayer(
  base: PsdLayer,
  node: LayerNode,
  maskProvider: PsdLayerMaskProvider | undefined,
  canvasWidth: number,
  canvasHeight: number,
): PsdLayer {
  if (!maskProvider || !node.mask) return base;
  const rawCanvas = maskProvider(node.id);
  if (!rawCanvas) return base;
  // The materialize pipeline emits RAW at full document size, white
  // where the mask is "on." If `inverted` is set, flip the pixels so
  // PSD viewers see the same alpha the editor showed.
  let outCanvas: HTMLCanvasElement = rawCanvas;
  if (node.mask.inverted) {
    const w = rawCanvas.width;
    const h = rawCanvas.height;
    const flipped = document.createElement('canvas');
    flipped.width = w;
    flipped.height = h;
    const fctx = flipped.getContext('2d');
    if (fctx) {
      // Copy then invert via difference. `destination-out` with
      // opaque-white drawn on a white-filled canvas wouldn't give
      // us per-pixel invert — fall back to the explicit pixel loop
      // for correctness. Mask canvases are document-sized; for 4K
      // that's ~16M pixels and a single ImageData pass is well
      // under a frame.
      fctx.drawImage(rawCanvas, 0, 0);
      const id = fctx.getImageData(0, 0, w, h);
      const data = id.data;
      for (let i = 0; i < data.length; i += 4) {
        data[i] = 255 - data[i];
        data[i + 1] = 255 - data[i + 1];
        data[i + 2] = 255 - data[i + 2];
      }
      fctx.putImageData(id, 0, 0);
      outCanvas = flipped;
    }
  }
  return {
    ...base,
    mask: {
      top: 0,
      left: 0,
      bottom: canvasHeight,
      right: canvasWidth,
      // PSD default color is the fill outside the mask's pixel rect.
      // Editor masks are always full-canvas so the outside-the-rect
      // value never matters in practice — 0 (black = hidden) is the
      // conservative choice for any viewer that interprets it.
      defaultColor: 0,
      disabled: node.mask.enabled === false,
      positionRelativeToLayer: node.mask.linked !== false,
      userMaskDensity: Math.round(Math.max(0, Math.min(1, node.mask.density ?? 1)) * 255),
      userMaskFeather: node.mask.feather ?? 0,
      canvas: outCanvas,
    },
  };
}

/** Inverse of `mapBlendMode` in `psd-import.ts`. Every editor
 *  `BlendMode` round-trips to an ag-psd blend mode string. `source-
 *  over` (the editor's "Normal") maps to `'normal'`; the rest are
 *  1:1 with CSS-compositing names that match ag-psd's vocabulary. */
const EDITOR_TO_PSD_BLEND_MODE: Record<EditorBlendMode, PsdBlendMode> = {
  'source-over': 'normal',
  multiply: 'multiply',
  screen: 'screen',
  overlay: 'overlay',
  darken: 'darken',
  lighten: 'lighten',
  'color-dodge': 'color dodge',
  'color-burn': 'color burn',
  'hard-light': 'hard light',
  'soft-light': 'soft light',
  difference: 'difference',
  exclusion: 'exclusion',
  hue: 'hue',
  saturation: 'saturation',
  color: 'color',
  luminosity: 'luminosity',
};

function toPsdBlendMode(mode: EditorBlendMode | undefined): PsdBlendMode {
  if (!mode) return 'normal';
  return EDITOR_TO_PSD_BLEND_MODE[mode] ?? 'normal';
}

/** Flatten the original parsed Psd's tree into a pre-order DFS
 *  array. Index N in the result corresponds to the PSD layer the
 *  import walk assigned `psdLayerIndex: N` to — see the visit
 *  function in `psd-import.ts::buildEditorDocument`. This MUST
 *  stay aligned with the import walk's traversal; if the import
 *  changes its order, this needs to change in lockstep. */
export function flattenPsdLayersPreorderDFS(psd: Psd): PsdLayer[] {
  const out: PsdLayer[] = [];
  function walk(layer: PsdLayer) {
    out.push(layer);
    for (const child of layer.children ?? []) walk(child);
  }
  for (const top of psd.children ?? []) walk(top);
  return out;
}

/** Overlay the editor's extrinsic properties onto a base PSD layer.
 *  Position is overlaid when `payload` is supplied (stash-restored
 *  leaf layers — without this, a user moving a stash-backed layer
 *  silently reverts on export). For groups + adjustment layers the
 *  payload is omitted because their position has different semantics
 *  (PSD groups don't carry a bbox the same way; adjustment layers
 *  have no pixels). Returns a new object; input is not mutated. */
function applyEditorExtrinsic(
  base: PsdLayer,
  node: LayerNode,
  payload?: PsdExportRasterPayload | null,
): PsdLayer {
  const result: PsdLayer = {
    ...base,
    name: node.name,
    blendMode: toPsdBlendMode(node.blendMode),
    opacity: node.opacity ?? 1,
    hidden: !node.visible,
  };
  // Position overlay — only when the caller surfaces it AND the base
  // has a meaningful bbox. Compute right/bottom by preserving the
  // base's dimensions (since stash-restored layers keep original
  // pixel content, the width/height are unchanged).
  if (payload && typeof base.top === 'number' && typeof base.left === 'number') {
    const w = (base.right ?? 0) - base.left;
    const h = (base.bottom ?? 0) - base.top;
    result.left = payload.left;
    result.top = payload.top;
    result.right = payload.left + w;
    result.bottom = payload.top + h;
  }
  return result;
}

/** Emit a raster leaf layer from the editor — used when the layer
 *  was editor-created (no stash origin) OR its pixels were modified
 *  since import (`rasterModified === true`). */
function emitRasterLayer(node: LayerNode, payload: PsdExportRasterPayload): PsdLayer {
  const { canvas, left, top } = payload;
  return {
    name: node.name,
    canvas,
    top,
    left,
    right: left + canvas.width,
    bottom: top + canvas.height,
    blendMode: toPsdBlendMode(node.blendMode),
    opacity: node.opacity ?? 1,
    hidden: !node.visible,
  };
}

export interface BuildExportPsdOptions {
  /** The editor's current document tree (in top-first display order). */
  document: EditorDocument;
  /** The re-parsed original PSD structure (from `readPsd(stash.psdBytes)`).
   *  When null, every layer is emitted as raster — used when no
   *  stash exists (editor-created from scratch, imported via a
   *  non-PSD path, or the stash entry was lost / never saved). */
  originalPsd: Psd | null;
  /** Per-layer origin map from the stash. When null or missing an
   *  entry for a given LayerNode.id, that layer emits as raster. */
  layerOriginById: Record<string, PsdLayerOrigin> | null;
  /** Resolves a layer's editor-rendered pixel canvas + position.
   *  Returns null when the layer has no pixel content (e.g. an
   *  empty placeholder); the caller's walk skips those layers. */
  layerCanvasProvider: LayerCanvasProvider;
  /** Resolves a text layer to a native PSD text record. Optional
   *  — when omitted (or returns null for a given layer), text
   *  layers fall through to the raster-emit path the way they
   *  did before the native PSD text export landed. EditorPage
   *  wires this through by querying the Fabric IText bound to
   *  the layer; tests inject synthetic shapes. */
  layerTextProvider?: PsdLayerTextProvider;
  /** Resolves a layer's RAW mask canvas, when one exists. Optional
   *  — when omitted (or returns null), layer masks aren't emitted.
   *  See `PsdLayerMaskProvider` for the contract. */
  layerMaskProvider?: PsdLayerMaskProvider;
  /** Resolves a primitive shape layer to a VectorPath. Optional;
   *  when omitted, shapes fall back to raster. See
   *  `PsdLayerShapePathProvider` for the contract. */
  layerShapePathProvider?: PsdLayerShapePathProvider;
  /** Canvas dimensions (the editor's current canvas size). Used as
   *  the PSD output document dimensions. */
  canvasWidth: number;
  canvasHeight: number;
}

/** Per-layer outcome — surfaced via `buildExportPsdResult.summary`
 *  so the caller can show a pre-save preview ("3 layers preserved
 *  from original PSD; 2 saved as raster"). */
export interface PsdExportSummary {
  preservedFromStash: number;
  emittedAsRaster: number;
  /** Editor-created (no stash origin) layers, a subset of
   *  emittedAsRaster — broken out for the preview text. */
  editorCreated: number;
  /** Stash-backed layers whose `rasterModified` flag forced raster
   *  emission — a subset of `emittedAsRaster`. */
  rasterModified: number;
  /** Layers with no pixel content that were skipped entirely. */
  skipped: number;
}

export interface BuildExportPsdResult {
  psd: Psd;
  summary: PsdExportSummary;
}

/** Pure structural walk — produces the ag-psd `Psd` ready for
 *  `writePsd`, plus a summary of per-layer emission outcomes. */
export function buildExportPsd(opts: BuildExportPsdOptions): BuildExportPsdResult {
  const {
    document,
    originalPsd,
    layerOriginById,
    layerCanvasProvider,
    layerTextProvider,
    layerMaskProvider,
    layerShapePathProvider,
    canvasWidth,
    canvasHeight,
  } = opts;

  function buildLayer(node: LayerNode): PsdLayer | null {
    const layer = buildLayerInner(node);
    if (!layer) return null;
    // Tail-apply mask + effects + text-path marker emission. Must
    // come AFTER each branch's own logic (which may spread a stash
    // entry) so the editor-side fields write LAST — the editor's
    // current state is canonical for layers the user has touched,
    // regardless of what the stash carried.
    const withMask = applyMaskToLayer(layer, node, layerMaskProvider, canvasWidth, canvasHeight);
    const withFx = applyEffectsToLayer(withMask, node);
    return applyTextPathMarker(withFx, node.id);
  }

  const originalFlat = originalPsd ? flattenPsdLayersPreorderDFS(originalPsd) : [];

  // Pre-scan for text-on-path bindings. Each text node with a
  // `textPath` gets a shared random token; the token is appended to
  // BOTH the text layer's and its target path layer's display name
  // via the `applyTextPathMarker` step below. Import-side detection
  // (in `psd-import.ts`) parses the markers back into a
  // `textPath: { pathLayerId }` binding and strips them from the
  // user-visible name.
  //
  // ⚠️ INTENDED AS A V1 WORKAROUND. ag-psd's `textPath` field is
  // marked read-only in its typings (`/** This is read-only field,
  // any changes will not be saved */` on `LayerTextData`). Until
  // ag-psd grows write support, this name-marker dance is the only
  // way path-bound text can round-trip through OUR editor. Files
  // viewed in Photoshop will show the marker text in the layer name
  // and the text + path as separate layers (no curve following).
  // Proper fix: upstream PR to ag-psd to make `textPath` writable.
  const textPathBindings = new Map<string, string>(); // editorLayerId → marker payload
  let textPathTokenCounter = 0;
  for (const id in document.layers) {
    const n = document.layers[id];
    if (n.type === 'text' && n.textPath?.pathLayerId) {
      const token = `tp${++textPathTokenCounter}_${Math.random().toString(36).slice(2, 8)}`;
      textPathBindings.set(id, `bind=${token}`);
      textPathBindings.set(n.textPath.pathLayerId, `anchor=${token}`);
    }
  }
  function applyTextPathMarker(layer: PsdLayer, nodeId: string): PsdLayer {
    const marker = textPathBindings.get(nodeId);
    if (!marker) return layer;
    const tag = `⟪fias:tp-${marker}⟫`;
    // Only append if the marker isn't already there (defensive
    // against double-pass via session restore + a follow-up save).
    if (layer.name?.includes(tag)) return layer;
    return { ...layer, name: `${layer.name ?? ''} ${tag}`.trim() };
  }
  const summary: PsdExportSummary = {
    preservedFromStash: 0,
    emittedAsRaster: 0,
    editorCreated: 0,
    rasterModified: 0,
    skipped: 0,
  };

  function buildLayerInner(node: LayerNode): PsdLayer | null {
    if (node.type === 'group') {
      // Recurse children. Editor's childIds is top-first; PSD's
      // children is bottom-to-top. Reverse on emit.
      const childIds = node.childIds ?? [];
      const psdChildren: PsdLayer[] = [];
      for (let i = childIds.length - 1; i >= 0; i--) {
        const child = document.layers[childIds[i]];
        if (!child) continue;
        const childLayer = buildLayer(child);
        if (childLayer) psdChildren.push(childLayer);
      }
      // If the group itself had a stash origin, preserve its PSD-
      // level chunks (sectionDivider markers, group metadata, etc.).
      // Otherwise synthesize a minimal group shape.
      const origin = layerOriginById?.[node.id];
      const originalGroup = origin ? originalFlat[origin.psdLayerIndex] : undefined;
      const base: PsdLayer = originalGroup
        ? { ...originalGroup, children: psdChildren }
        : { name: node.name, children: psdChildren };
      return applyEditorExtrinsic(base, node);
    }

    // Adjustment layers have no pixel content — they reference an
    // operation, not a raster. Resolution priority:
    //
    //  1. Editor has a current `AdjustmentConfig` (the user may have
    //     edited it) → reverse-map back to ag-psd's `AdjustmentLayer`
    //     shape via `unmapEditorAdjustment`. This is the EDIT-PRESERVING
    //     path — without it, a Curves adjustment whose curve points
    //     were edited would silently revert to the stash's original
    //     on save.
    //
    //  2. Stash entry exists but reverse-map returned null (kind not
    //     yet supported on export — selective-color, channel-mixer,
    //     filter-*) → restore from stash verbatim.
    //
    //  3. Neither → skip.
    if (node.type === 'adjustment') {
      const origin = layerOriginById?.[node.id];
      const stashed = origin ? originalFlat[origin.psdLayerIndex] : undefined;
      const editorAdjustment = node.adjustment ? unmapEditorAdjustment(node.adjustment) : null;
      if (editorAdjustment) {
        // Edit-preserving path: emit the layer with the editor's
        // current adjustment config under the `adjustment` field
        // (NOT spread — ag-psd's Layer carries the AdjustmentLayer
        // shape as a nested chunk, not flattened top-level fields).
        // When a stash original exists we use it as the base so
        // group-level metadata (sectionDivider markers, layer-comp
        // visibility) survives — only the adjustment chunk is
        // overwritten by the edited config.
        const base: PsdLayer = stashed
          ? { ...stashed, adjustment: editorAdjustment }
          : { name: node.name, adjustment: editorAdjustment };
        summary.preservedFromStash += 1;
        return applyEditorExtrinsic(base, node);
      }
      if (stashed) {
        summary.preservedFromStash += 1;
        return applyEditorExtrinsic(stashed, node);
      }
      // Editor-created adjustment whose kind has no PSD AdjustmentLayer
      // counterpart — the `filter-*` blurs, shadows-highlights, the
      // not-yet-wired selective-color / channel-mixer. The slow-path
      // compositor has already baked the layer's effect into its
      // backing canvas, so emit that canvas as a raster layer. The
      // visual round-trips faithfully; the parametric editability is
      // lost (the same trade-off Photoshop makes when saving Smart
      // Filters to non-PSB formats). Previously these layers were
      // silently dropped — a "saved my radial blur, opened the PSD,
      // it's gone" surprise reported 2026-05-25.
      const payload = layerCanvasProvider(node.id);
      if (payload) {
        summary.emittedAsRaster += 1;
        summary.editorCreated += 1;
        return emitRasterLayer(node, payload);
      }
      summary.skipped += 1;
      return null;
    }

    // Vector layer — editor-canonical via VectorPath (PSD natives
    // follow-up, Phase C). Emit as a fresh PSD layer with
    // `vectorMask` + `vectorFill` + `vectorStroke` chunks instead
    // of a raster. Stash origin is informational only; vector
    // edits update `node.path` directly without a rasterModified
    // flag, so the editor's path is canonical.
    if (node.type === 'vector' && node.path) {
      const chunks = buildPsdVectorChunks(node.path);
      if (chunks) {
        const origin = layerOriginById?.[node.id];
        const stashed = origin ? originalFlat[origin.psdLayerIndex] : undefined;
        const base: PsdLayer = stashed ? { ...stashed, ...chunks } : { name: node.name, ...chunks };
        summary.emittedAsRaster += 1; // counted as native non-raster emission
        if (!origin) summary.editorCreated += 1;
        return applyEditorExtrinsic(base, node);
      }
      // Empty path — skip entirely.
      summary.skipped += 1;
      return null;
    }

    // Shape layer (Rect / Circle / Triangle — Fabric primitive
    // backed). Convert the Fabric object's geometry to a VectorPath
    // via `layerShapePathProvider` and emit the same PSD vector
    // chunks the vector branch above uses. Without this branch,
    // shapes silently rasterize and lose parametric round-trip:
    // corner radius, dimensions, stroke width — all gone after a
    // PSD save/load. (Line shapes return null from the provider
    // and fall through to raster — PSD shape layers expect closed
    // regions and a thin-rect approximation isn't worth the
    // complexity for v1.)
    if (node.type === 'shape' && layerShapePathProvider) {
      const path = layerShapePathProvider(node.id);
      if (path) {
        const chunks = buildPsdVectorChunks(path);
        if (chunks) {
          const origin = layerOriginById?.[node.id];
          const stashed = origin ? originalFlat[origin.psdLayerIndex] : undefined;
          const base: PsdLayer = stashed
            ? { ...stashed, ...chunks }
            : { name: node.name, ...chunks };
          summary.emittedAsRaster += 1;
          if (!origin) summary.editorCreated += 1;
          return applyEditorExtrinsic(base, node);
        }
      }
      // Provider returned null (line / future shape type) — fall
      // through to raster.
    }

    // Text layer — try native first (PSD natives follow-up,
    // Phase C). When `layerTextProvider` is wired AND returns a
    // payload for this layer, emit as a fresh PSD text layer
    // with a reconstructed LayerTextData record. Provider returning
    // null falls back to raster — used for arched / on-path /
    // multi-line IText state that the native emitter can't carry.
    if (node.type === 'text' && layerTextProvider) {
      const textPayload = layerTextProvider(node.id);
      if (textPayload) {
        const psdText = buildPsdTextRecord(textPayload);
        const origin = layerOriginById?.[node.id];
        const stashed = origin ? originalFlat[origin.psdLayerIndex] : undefined;
        const base: PsdLayer = stashed
          ? { ...stashed, text: psdText }
          : { name: node.name, text: psdText };
        summary.emittedAsRaster += 1; // counted as native non-raster
        if (!origin) summary.editorCreated += 1;
        return applyEditorExtrinsic(base, node);
      }
      // Provider returned null — fall through to raster.
    }

    // Leaf layer with pixel content — decide stash vs raster.
    // Resolve the editor payload up front so the stash branch can
    // overlay the editor's current position on the restored layer
    // (otherwise a layer moved in the editor would silently revert
    // to its original PSD position on save). When the payload is
    // null AND we're stash-restoring, we proceed without position
    // overlay (the layer is no longer on the canvas for whatever
    // reason — defensive fallback).
    const origin = layerOriginById?.[node.id];
    const stashed = origin ? originalFlat[origin.psdLayerIndex] : undefined;
    const canRestoreFromStash = stashed && !node.rasterModified && originalPsd;
    const payload = layerCanvasProvider(node.id);
    if (canRestoreFromStash) {
      summary.preservedFromStash += 1;
      return applyEditorExtrinsic(stashed, node, payload);
    }

    // Raster emission — payload is required (provides the pixels).
    if (!payload) {
      summary.skipped += 1;
      return null;
    }
    summary.emittedAsRaster += 1;
    if (origin && node.rasterModified) summary.rasterModified += 1;
    else if (!origin) summary.editorCreated += 1;
    return emitRasterLayer(node, payload);
  }

  // Reverse rootIds: editor top-first → PSD bottom-to-top.
  const psdChildren: PsdLayer[] = [];
  for (let i = document.rootIds.length - 1; i >= 0; i--) {
    const node = document.layers[document.rootIds[i]];
    if (!node) continue;
    const layer = buildLayer(node);
    if (layer) psdChildren.push(layer);
  }

  // Preserve doc-level metadata by spreading the original Psd and
  // overriding only what the editor controls (dimensions + children).
  // When no stash exists, synthesize a minimal 8-bit RGB Psd shape.
  const psd: Psd = originalPsd
    ? { ...originalPsd, width: canvasWidth, height: canvasHeight, children: psdChildren }
    : {
        width: canvasWidth,
        height: canvasHeight,
        bitsPerChannel: 8,
        colorMode: 3, // RGB
        children: psdChildren,
      };
  return { psd, summary };
}

// ---------- Orchestrator ----------------------------------------------------

export interface ExportDocumentToPsdOptions {
  documentId: string;
  document: EditorDocument;
  canvasWidth: number;
  canvasHeight: number;
  layerCanvasProvider: LayerCanvasProvider;
  /** Forwarded to `buildExportPsd` — see that function's docstring
   *  for native PSD `<text>` emission. Optional; when omitted, text
   *  layers route through the existing raster fallback. */
  layerTextProvider?: PsdLayerTextProvider;
  /** Forwarded to `buildExportPsd` — see `PsdLayerMaskProvider`.
   *  Optional; when omitted, layer masks aren't emitted. */
  layerMaskProvider?: PsdLayerMaskProvider;
  /** Forwarded to `buildExportPsd` — see `PsdLayerShapePathProvider`.
   *  Optional; when omitted, primitive shape layers rasterize. */
  layerShapePathProvider?: PsdLayerShapePathProvider;
}

export interface ExportDocumentToPsdResult {
  /** PSD bytes ready for download / Fias upload / etc. */
  bytes: ArrayBuffer;
  /** Per-layer outcome summary — surface to the user so they know
   *  what was preserved losslessly vs flattened. */
  summary: PsdExportSummary;
  /** True when an original PSD stash was available and used as the
   *  base for the export. False when synthesizing from scratch. */
  hadStash: boolean;
}

/** Bytes-out entry point. Looks up the PSD stash, re-parses it to
 *  recover the original layer-data tree, runs the merge algorithm,
 *  and serializes via ag-psd's `writePsd`.
 *
 *  Dynamic-imports `ag-psd` to match the import path's bundle
 *  strategy — non-PSD users don't pay the cost on initial load. */
export async function exportDocumentToPsd(
  opts: ExportDocumentToPsdOptions,
): Promise<ExportDocumentToPsdResult> {
  const {
    documentId,
    document,
    canvasWidth,
    canvasHeight,
    layerCanvasProvider,
    layerTextProvider,
    layerMaskProvider,
    layerShapePathProvider,
  } = opts;

  const [agPsd, stash] = await Promise.all([import('ag-psd'), loadPsdStash(documentId)]);

  let originalPsd: Psd | null = null;
  if (stash) {
    // Re-parse the stash bytes to get the full PSD layer tree with
    // unmapped chunks intact. `useImageData: true` keeps layer pixel
    // data as Uint8Array PixelData rather than allocating a fresh
    // HTMLCanvasElement per layer — critical for memory on large
    // PSDs (a 500MB PSD with 100 layers would otherwise allocate
    // GB-scale canvas memory during the re-parse). The merge only
    // reads layer metadata + chunks; writePsd accepts either canvas
    // or imageData when re-emitting, so the round-trip stays valid.
    originalPsd = agPsd.readPsd(stash.psdBytes, {
      skipCompositeImageData: true,
      skipThumbnail: true,
      useImageData: true,
    });
  }

  const { psd, summary } = buildExportPsd({
    document,
    originalPsd,
    layerOriginById: stash?.layerOriginById ?? null,
    layerCanvasProvider,
    layerTextProvider,
    layerMaskProvider,
    layerShapePathProvider,
    canvasWidth,
    canvasHeight,
  });

  // writePsd returns ArrayBuffer in browser environments.
  const bytes = agPsd.writePsd(psd, {
    // The editor doesn't display the PSD's composite-thumbnail, but
    // other PSD viewers (Photoshop's file picker, Bridge, OS Finder
    // preview) all show the thumbnail. Generate one so saved files
    // preview correctly across the ecosystem.
    generateThumbnail: true,
    // Avoid Adobe's "Maximize Compatibility" full-composite flatten
    // — it doubles file size for no functional gain. Modern Photoshop
    // (CC and later) opens layer-only PSDs without issue.
    psb: false,
  }) as ArrayBuffer;

  return {
    bytes,
    summary,
    hadStash: stash !== null,
  };
}
